import React, { useState, useEffect } from "react";
import { API_URL } from "../config";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Notification from "../components/Notification.js";

function MinistryDonation() {
    const [loading, setLoading] = useState(false);
    const [mpesaPhoneNumber, setMpesaPhoneNumber] = useState("");
    const [amount, setAmount] = useState("");
    const [email, setEmail] = useState("");
    const [donationType, setDonationType] = useState("one-time");
    const [frequency, setFrequency] = useState("monthly");
    const [reminderDate, setReminderDate] = useState("1");
    const [error, setError] = useState("");
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState("");
    const [broadcastToken, setBroadcastToken] = useState(null);
    const [donationId, setDonationId] = useState(null);
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [verificationCode, setVerificationCode] = useState("");
    const [verificationPhone, setVerificationPhone] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("mpesa");
    const [bankTransferDetails, setBankTransferDetails] = useState({
        transactionId: "",
        date: "",
        amount: ""
    });
    const token = localStorage.getItem("access_token");

    // Check if user is logged in and populate fields
    useEffect(() => {
        const userData = localStorage.getItem("user");

        if (token && userData) {
            setIsLoggedIn(true);
            try {
                const user = JSON.parse(userData);
                if (user.phone_number) {
                    // Remove leading 0 or country code if present
                    let phone = user.phone_number;
                    if (phone.startsWith("0")) {
                        phone = phone.substring(1);
                    } else if (phone.startsWith("254")) {
                        phone = phone.substring(3);
                    }
                    setMpesaPhoneNumber(phone);
                }
                if (user.email) {
                    setEmail(user.email);
                }
            } catch (e) {
                console.error("Error parsing user data:", e);
            }
        }
    }, [token]);

    // Generate date options based on frequency
    const getDateOptions = () => {
        switch (frequency) {
            case "weekly":
                return Array.from({ length: 7 }, (_, i) => ({
                    value: (i + 1).toString(),
                    label: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][i]
                }));
            case "monthly":
                return Array.from({ length: 28 }, (_, i) => ({
                    value: (i + 1).toString(),
                    label: `${i + 1}${getOrdinalSuffix(i + 1)}`
                }));
            case "yearly":
                return Array.from({ length: 12 }, (_, i) => ({
                    value: (i + 1).toString(),
                    label: new Date(2023, i, 1).toLocaleString('default', { month: 'long' })
                }));
            default:
                return [];
        }
    };

    const getOrdinalSuffix = (num) => {
        const j = num % 10;
        const k = num % 100;
        if (j === 1 && k !== 11) return 'st';
        if (j === 2 && k !== 12) return 'nd';
        if (j === 3 && k !== 13) return 'rd';
        return 'th';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // Clear previous messages
        setError("");
        setSuccessMessage("");
        setErrors({}); // Clear previous errors

        // Validate phone number
        const strippedNumber = mpesaPhoneNumber.startsWith("0")
            ? mpesaPhoneNumber.slice(1)
            : mpesaPhoneNumber;

        if (!/^[1-9][0-9]{8}$/.test(strippedNumber)) {
            setError("Invalid phone number.");
            return;
        }

        // Validate amount
        if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
            setError("Please enter a valid donation amount.");
            return;
        }

        // Validate email for recurring donations
        if (donationType === "recurring") {
            if (!email) {
                setError("Please provide your email for recurring donations.");
                return;
            }
            if (!/^\S+@\S+\.\S+$/.test(email)) {
                setError("Please enter a valid email address.");
                return;
            }
        }

        setLoading(true);

        try {
            // Create the payload object according to API requirements
            const payload = {
                phone_number: strippedNumber,
                amount: parseFloat(amount),
                type: donationType,
                payment_method: "mpesa",
                ...(donationType === "recurring" && {
                    frequency: frequency,
                    reminder_date: reminderDate,
                    donor_email: email
                })
            };

            // Remove null or empty fields
            const filteredPayload = Object.keys(payload).reduce((acc, key) => {
                if (payload[key] !== null && payload[key] !== "") {
                    acc[key] = payload[key];
                }
                return acc;
            }, {});

            const response = await fetch(`${API_URL}/donations/pay`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(filteredPayload),
            });

            if (response.ok) {
                const data = await response.json();
                setLoading(true);
                setBroadcastToken(data.broadcastToken);
                setDonationId(data.donationId);
                setSuccessMessage(data.message);
            } else {
                const errorData = await response.json();
                console.log("Error response:", errorData);
                setError(errorData.message || "An error occurred while processing your request.");
                setErrors(errorData || {});
                setNotification({ message: errorData.message || "An error occurred.", type: "danger" });

            }
        } catch (err) {
            console.log(err);
            setError("An unexpected error occurred.");
            setNotification({ type: "danger", message: "An unexpected error occurred. Please try again later." });
        } finally {
            setLoading(false);
        }
    };

    const handleBankTransferSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const payload = {
                bank_reference: bankTransferDetails.transactionId,
                bank_transfer_date: bankTransferDetails.date,
                amount: bankTransferDetails.amount,
                type: donationType,
                payment_method: "bank_transfer",
                phone_number: mpesaPhoneNumber,
                donor_email: email,
                ...(donationType === "recurring" && {
                    frequency: frequency,
                    reminder_date: reminderDate
                })
            };

            // Remove null or empty fields
            const filteredPayload = Object.keys(payload).reduce((acc, key) => {
                if (payload[key] !== null && payload[key] !== "") {
                    acc[key] = payload[key];
                }
                return acc;
            }, {});

            const response = await fetch(`${API_URL}/donations/pay`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(filteredPayload),
            });

            const data = await response.json();

            if (response.ok) {
                setNotification({
                    type: "success",
                    message: data.message || "Thank you! We've recorded your bank transfer and will send reminders accordingly."
                });
            } else {
                setNotification({
                    type: "danger",
                    message: data.message || "Failed to record bank transfer details"
                });
            }
        } catch (err) {
            setNotification({
                type: "danger",
                message: "An error occurred while processing your request"
            });
        } finally {
            setLoading(false);
        }
    };

    const checkPaymentStatus = async (broadcastToken, startTime = Date.now()) => {
        setLoading(true);
        // Calculate elapsed time
        const elapsed = Date.now() - startTime;

        // Stop polling if 1 minute (60,000 ms) has passed
        if (elapsed > 60000) {
            setNotification({ type: "danger", message: "Payment verification timed out. Please try again" });
            setError("Payment verification timed out. Please try again")
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${API_URL}/donations/${donationId}/status`,);
            const data = await response.json();

            if (data.status === "success") {
                setNotification({ type: "success", message: data.message || "Donation successful! Thank you for your support." });
                setSuccessMessage(data.message || "Donation successful! Thank you for your support.");
                setMpesaPhoneNumber("");
                setAmount("");
            } else {
                // Try again after 5 seconds
                setTimeout(() => {
                    checkPaymentStatus(broadcastToken, startTime);
                }, 5000);
            }
        } catch (err) {
            setNotification({ type: "danger", message: "Error checking payment status" });
        }
    };

    const handleVerifyPayment = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            const payload = {
                phone_number: verificationPhone,
                payment_code: verificationCode,
                donation_id: donationId
            };

            // Remove null or empty fields
            const filteredPayload = Object.keys(payload).reduce((acc, key) => {
                if (payload[key] !== null && payload[key] !== "") {
                    acc[key] = payload[key];
                }
                return acc;
            }, {});

            const response = await fetch(`${API_URL}/donations/payment-verification`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(filteredPayload),
            });

            if (response.ok) {
                const data = await response.json();
                setLoading(true);
                setNotification({ type: "success", message: "Payment verified successfully!" });
            } else {
                setNotification({ type: "danger", message: "Verification failed" });
            }
        } catch (err) {
            setNotification({ type: "danger", message: "Network error during verification" });
        }
    };

    useEffect(() => {
        if (broadcastToken) {
            setTimeout(() => {
                checkPaymentStatus(broadcastToken);
            }, 3000);
        }
    }, [broadcastToken]);

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };


    // Update bank transfer form
    const handleBankTransferChange = (e) => {
        const { name, value } = e.target;
        setBankTransferDetails(prev => ({
            ...prev,
            [name]: value
        }));
    };

    return (
        <div>
            <Header />
            <div className="container mt-0">
                <div className="section">
                    {notification.message && (
                        <Notification
                            type={notification.type}
                            message={notification.message}
                            onClose={closeNotification}
                        />
                    )}

                    <div className="section box">
                        <h1 className="title is-2 has-text-centered">Support Our Ministry</h1>

                        {/* Payment Method Toggle */}
                        <div className="field has-text-centered mb-5">
                            <div className="buttons has-addons is-centered">
                                <button
                                    className={`button ${paymentMethod === "mpesa" ? "is-primary" : "is-light"}`}
                                    onClick={() => setPaymentMethod("mpesa")}
                                >
                                    M-Pesa Payment
                                </button>
                                <button
                                    className={`button ${paymentMethod === "bank" ? "is-primary" : "is-light"}`}
                                    onClick={() => setPaymentMethod("bank")}
                                >
                                    Bank Transfer
                                </button>
                            </div>
                        </div>

                        {paymentMethod === "mpesa" ? (
                            <div className="columns is-multiline">
                                <div className="column is-half">
                                    <div className="box">
                                        <h2 className="title is-4">M-Pesa Payment</h2>
                                        <form onSubmit={handleSubmit}>
                                            {/* Donation Type */}
                                            <div className="field">
                                                <label className="label">Donation Type</label>
                                                <div className="control">
                                                    <div className="select is-fullwidth">
                                                        <select
                                                            value={donationType}
                                                            onChange={(e) => setDonationType(e.target.value)}
                                                        >
                                                            <option value="one-time">One-time Donation</option>
                                                            <option value="recurring">Recurring Donation</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Frequency (for recurring donations) */}
                                            {donationType === "recurring" && (
                                                <>
                                                    <div className="field">
                                                        <label className="label">Frequency</label>
                                                        <div className="control">
                                                            <div className="select is-fullwidth">
                                                                <select
                                                                    value={frequency}
                                                                    onChange={(e) => {
                                                                        setFrequency(e.target.value);
                                                                        setReminderDate("1"); // Reset to first option when frequency changes
                                                                    }}
                                                                >
                                                                    <option value="weekly">Weekly</option>
                                                                    <option value="monthly">Monthly</option>
                                                                    <option value="yearly">Yearly</option>
                                                                </select>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Reminder Date Selection */}
                                                    <div className="field">
                                                        <label className="label">
                                                            {frequency === "weekly" ? "Remind me every" :
                                                                frequency === "monthly" ? "Day of month to donate" :
                                                                    "Month to donate"}
                                                        </label>
                                                        <div className="control">
                                                            <div className="select is-fullwidth">
                                                                <select
                                                                    value={reminderDate}
                                                                    onChange={(e) => setReminderDate(e.target.value)}
                                                                >
                                                                    {getDateOptions().map(option => (
                                                                        <option key={option.value} value={option.value}>
                                                                            {option.label}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </div>
                                                        <p className="help">
                                                            {frequency === "weekly" ?
                                                                "We'll send you a reminder to make donation every selected day" :
                                                                frequency === "monthly" ?
                                                                    "We'll send you a reminder to make donation on this day each month" :
                                                                    "We'll send you a reminder to make donation in this month each year"}
                                                        </p>
                                                    </div>

                                                    {/* Email */}
                                                    <div className="field">
                                                        <label className="label">Email Address</label>
                                                        <div className="control">
                                                            <input
                                                                className="input"
                                                                type="email"
                                                                placeholder="Your email for reminders"
                                                                value={email}
                                                                onChange={(e) => setEmail(e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                        <p className="help">We'll send you reminders before each donation</p>
                                                    </div>
                                                </>
                                            )}

                                            {/* Amount */}
                                            <div className="field">
                                                <label className="label">Amount (KES)</label>
                                                <div className="control">
                                                    <input
                                                        className={`input ${errors.amount ? "is-danger" : ""}`}
                                                        type="number"
                                                        placeholder="Enter amount (minimum 10 KES)"
                                                        value={amount}
                                                        onChange={(e) => setAmount(e.target.value)}
                                                        required
                                                        min="10"
                                                    />
                                                </div>
                                                {errors.amount && (
                                                    <p className="help is-danger">{errors.amount[0]}</p>
                                                )}
                                            </div>

                                            {/* Phone Number */}
                                            <div className="field">
                                                <label className="label">M-Pesa Phone Number</label>
                                                <div className="control is-flex has-addons">
                                                    <span className="button is-static">+254</span>
                                                    <input
                                                        className={`input ${error ? "is-danger" : ""}`}
                                                        type="text"
                                                        placeholder="e.g. 712345678"
                                                        value={mpesaPhoneNumber}
                                                        onChange={(e) => setMpesaPhoneNumber(e.target.value)}
                                                        required
                                                        disabled={loading}
                                                    />
                                                </div>
                                            </div>

                                            {error && <p className="help is-danger">{error}</p>}
                                            {successMessage && <p className="help is-success">{successMessage}</p>}

                                            <div className="field mt-5">
                                                <div className="control">
                                                    <button
                                                        className={`button is-primary is-medium is-fullwidth ${loading ? "is-loading" : ""}`}
                                                        type="submit"
                                                        disabled={loading}
                                                    >
                                                        {donationType === "one-time" ? "Make Donation" : "Setup Recurring Donation"}
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>

                                <div className="column is-half">
                                    <div className="box">


                                        {/* Add this right after your M-Pesa form (inside the same box div) */}
                                        <div className="mt-5">
                                            <div className="notification is-warning">
                                                <h4 className="title is-5">Payment Not Confirmed?</h4>
                                                <p>If money was deducted from your account but the system gave an error, you can verify payment here:<br /><br /> </p>

                                                <form onSubmit={handleVerifyPayment}>
                                                    <div className="field">
                                                        <label className="label">M-Pesa Transaction Code</label>
                                                        <div className="control">
                                                            <input
                                                                className="input"
                                                                type="text"
                                                                placeholder="e.g. MFD123ABC"
                                                                value={verificationCode}
                                                                onChange={(e) => setVerificationCode(e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="field">
                                                        <label className="label">Phone Number Used</label>
                                                        <div className="control is-flex has-addons">
                                                            <span className="button is-static">+254</span>
                                                            <input
                                                                className="input"
                                                                type="text"
                                                                placeholder="e.g. 712345678"
                                                                value={verificationPhone}
                                                                onChange={(e) => setVerificationPhone(e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                    </div>

                                                    <button
                                                        type="submit"
                                                        className="button is-warning is-fullwidth"
                                                        disabled={!verificationCode || !verificationPhone}
                                                    >
                                                        <span className="icon">
                                                            <i className="fas fa-check-circle"></i>
                                                        </span>
                                                        <span>Verify Payment Manually</span>
                                                    </button>
                                                </form>
                                            </div>
                                        </div>

                                    </div>
                                </div>

                            </div>



                        ) : (
                            <div className="columns is-multiline">
                                {/* Bank Transfer Instructions */}
                                <div className="column is-half">
                                    <div className="box">
                                        <h2 className="title is-4">Bank Transfer Details</h2>
                                        <div className="content">
                                            <p>You can make a deposit via:</p>

                                            <h3 className="title is-5 mt-4">1. Prime Bank</h3>
                                            <ul>
                                                <li><strong>Account Name:</strong> Commissioners For Christ Ministry</li>
                                                <li><strong>Account Number:</strong> 2000062715</li>
                                            </ul>

                                            <h3 className="title is-5 mt-4">2. Consolidated Bank</h3>
                                            <ul>
                                                <li><strong>Account Name:</strong> Commissioners For Christ Ministry</li>
                                                <li><strong>Account Number:</strong> 10021307000012</li>
                                            </ul>

                                            <h3 className="title is-5 mt-4">3. Prime Bank M-Pesa PayBill</h3>
                                            <ul>
                                                <li><strong>PayBill Number:</strong> 982800</li>
                                                <li><strong>Account Number:</strong> 2000062715</li>
                                            </ul>

                                            <div className="notification is-info mt-4">
                                                <p>After making your deposit or transfer, please complete the form on the right so we can:</p>
                                                <ol>
                                                    <li>Verify your payment</li>
                                                    <li>Send you a receipt</li>
                                                    <li>Setup reminders if this is a recurring donation</li>
                                                </ol>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Bank Transfer Form */}
                                <div className="column is-half">
                                    <div className="box">
                                        <h2 className="title is-4">Payment Details</h2>
                                        <form onSubmit={handleBankTransferSubmit}>
                                            {/* Donation Type */}
                                            <div className="field">
                                                <label className="label">Donation Type</label>
                                                <div className="control">
                                                    <div className="select is-fullwidth">
                                                        <select
                                                            value={donationType}
                                                            onChange={(e) => setDonationType(e.target.value)}
                                                        >
                                                            <option value="one-time">One-time Donation</option>
                                                            <option value="recurring">Recurring Donation</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Recurring Options */}
                                            {donationType === "recurring" && (
                                                <>
                                                    <div className="field">
                                                        <label className="label">Frequency</label>
                                                        <div className="control">
                                                            <div className="select is-fullwidth">
                                                                <select
                                                                    value={frequency}
                                                                    onChange={(e) => {
                                                                        setFrequency(e.target.value);
                                                                        setReminderDate("1");
                                                                    }}
                                                                >
                                                                    <option value="weekly">Weekly</option>
                                                                    <option value="monthly">Monthly</option>
                                                                    <option value="yearly">Yearly</option>
                                                                </select>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="field">
                                                        <label className="label">
                                                            {frequency === "weekly" ? "Remind me every" :
                                                                frequency === "monthly" ? "Day of month to remind" :
                                                                    "Month to remind"}
                                                        </label>
                                                        <div className="control">
                                                            <div className="select is-fullwidth">
                                                                <select
                                                                    value={reminderDate}
                                                                    onChange={(e) => setReminderDate(e.target.value)}
                                                                >
                                                                    {getDateOptions().map(option => (
                                                                        <option key={option.value} value={option.value}>
                                                                            {option.label}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            )}

                                            {/* Transaction Details */}
                                            <div className="field">
                                                <label className="label">Transaction Reference/ID</label>
                                                <div className="control">
                                                    <input
                                                        className="input"
                                                        type="text"
                                                        name="transactionId"
                                                        value={bankTransferDetails.transactionId}
                                                        onChange={handleBankTransferChange}
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            <div className="field">
                                                <label className="label">Amount Transferred (KES)</label>
                                                <div className="control">
                                                    <input
                                                        className="input"
                                                        type="number"
                                                        name="amount"
                                                        value={bankTransferDetails.amount}
                                                        onChange={handleBankTransferChange}
                                                        required
                                                        min="10"
                                                    />
                                                </div>
                                            </div>

                                            <div className="field">
                                                <label className="label">Transaction Date</label>
                                                <div className="control">
                                                    <input
                                                        className="input"
                                                        type="date"
                                                        name="date"
                                                        value={bankTransferDetails.date}
                                                        onChange={handleBankTransferChange}
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            {/* Contact Information */}
                                            <div className="field">
                                                <label className="label">Email Address</label>
                                                <div className="control">
                                                    <input
                                                        className="input"
                                                        type="email"
                                                        value={email}
                                                        onChange={(e) => setEmail(e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <p className="help">
                                                    {donationType === "recurring"
                                                        ? "We'll send reminders to this address"
                                                        : "We'll send your receipt here"}
                                                </p>
                                            </div>

                                            <div className="field">
                                                <label className="label">Phone Number (Optional)</label>
                                                <div className="control is-flex has-addons">
                                                    <span className="button is-static">+254</span>
                                                    <input
                                                        className="input"
                                                        type="text"
                                                        placeholder="e.g. 712345678"
                                                        value={mpesaPhoneNumber}
                                                        onChange={(e) => setMpesaPhoneNumber(e.target.value)}
                                                    />
                                                </div>
                                                <p className="help">For SMS notifications if preferred</p>
                                            </div>

                                            <div className="field mt-5">
                                                <div className="control">
                                                    <button
                                                        className={`button is-primary is-medium is-fullwidth ${loading ? "is-loading" : ""}`}
                                                        type="submit"
                                                        disabled={loading}
                                                    >
                                                        Submit Payment Details
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
}

export default MinistryDonation;